import { Flex, Modal, Typography } from "antd"
import { useTranslation } from "react-i18next"

import FallbackMarker from "../IconCell/FallbackMarker"

import styles from "./ConfirmDialog.module.less"

const { Text } = Typography

/**
 * The confirmation shown before any name or icon change is written.
 *
 * A name change renders its before and after side by side, with an explicit marker where a side is
 * empty — blank space would not read as "the name was removed". An icon action instead lists every
 * raw value it reaches, because a group-wide action goes well beyond the row the menu was opened on.
 */
const ConfirmDialog = ({
  open,
  kind,
  record,
  nameBefore,
  nameAfter,
  affectedRawValues,
  busy,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation()

  if (!record) return null

  const copy = {
    rename: { title: t("Save display name?"), okText: t("Save"), danger: false },
    replaceIcon: { title: t("Replace icon?"), okText: t("Replace"), danger: false },
    deleteIcon: { title: t("Delete icon?"), okText: t("Delete"), danger: true }
  }[kind]

  const emptyMarker = (
    <Text type="secondary" className={styles.empty}>
      {t("empty")}
    </Text>
  )

  return (
    <Modal
      open={open}
      title={copy.title}
      okText={copy.okText}
      // Every modal gets a patterned background unless it opts out with clear-bg.
      classNames={{ content: "clear-bg" }}
      cancelText={t("Cancel")}
      okButtonProps={{ danger: copy.danger, loading: busy }}
      onOk={onConfirm}
      onCancel={onCancel}
    >
      <Flex vertical gap={12}>
        <Text>
          {t("Value")}: <Text strong>{record.current_name}</Text>
        </Text>

        {kind === "rename" ? (
          <Flex align="center" gap={8} wrap="wrap">
            {nameBefore ? <Text delete>{nameBefore}</Text> : emptyMarker}
            <Text type="secondary">→</Text>
            {nameAfter ? <Text strong>{nameAfter}</Text> : emptyMarker}
          </Flex>
        ) : null}

        {kind === "deleteIcon" ? (
          <Flex align="center" gap={8}>
            <Text type="secondary">{t("After deletion the rows fall back to")}</Text>
            <FallbackMarker />
          </Flex>
        ) : null}

        {affectedRawValues?.length > 1 ? (
          <div>
            {/* Support must see that the action reaches beyond the row they clicked. */}
            <Text type="secondary">{t("This applies to every value shown as one row:")}</Text>
            <ul className={styles.affected}>
              {affectedRawValues.map((value) => (
                <li key={value}>
                  <Text>{value}</Text>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Flex>
    </Modal>
  )
}

export default ConfirmDialog
